/**
 * Utility for managing which user is being viewed in team management
 * Uses database storage instead of localStorage
 */
import { getSystemData, saveSystemData, clearFromDiskStorage } from './storage';
import fileLogger from './logger';
import { createErrorLogger, ErrorType } from './errorHandler';

const logger = createErrorLogger('ViewedUserStore');

const VIEWED_USER_KEY = 'viewed_user_id';
const VIEW_CONTEXT_KEY = 'view_context';

// Constants for different view contexts
export const VIEW_CONTEXTS = {
  TEAM_MANAGEMENT: 'team_management',
  OWN_PROFILE: 'own_profile',
  ADMIN_VIEW: 'admin_view'
};

/**
 * Set the currently viewed user ID
 * @param {string} userId - The ID of the user being viewed
 * @param {string} viewContext - The view context
 * @returns {Promise<void>}
 */
export const saveViewedUser = async (userId, viewContext = VIEW_CONTEXTS.TEAM_MANAGEMENT) => {
  try {
    if (!userId) {
      logger.warn('saveViewedUser', 'Attempting to set viewed user with empty ID');
      fileLogger.warn('VIEWED_USER_STORE', 'Attempting to set viewed user with empty ID');
      return;
    }

    logger.info('saveViewedUser', 'Setting viewed user', { userId, viewContext });
    fileLogger.info('VIEWED_USER_STORE', 'Setting viewed user', { userId });
    await saveSystemData(VIEWED_USER_KEY, userId);
    await saveSystemData(VIEW_CONTEXT_KEY, viewContext);
  } catch (error) {
    logger.error('saveViewedUser', 'Error saving viewed user ID', error, ErrorType.DATA_SAVE);
    fileLogger.error('VIEWED_USER_STORE', 'Error setting viewed user', { userId, error: error.message });
  }
};

/**
 * Get the currently viewed user ID
 * @returns {Promise<string|null>} - The ID of the user being viewed, or null if not set
 */
export const getViewedUser = async () => {
  try {
    const userId = await getSystemData(VIEWED_USER_KEY, null);
    // logger.debug('getViewedUser', 'Getting viewed user', { userId });
    fileLogger.debug('VIEWED_USER_STORE', 'Getting viewed user', { userId });
    return userId;
  } catch (error) {
    logger.error('getViewedUser', 'Error getting viewed user ID', error, ErrorType.DATA_LOAD);
    fileLogger.error('VIEWED_USER_STORE', 'Error getting viewed user', { error: error.message });
    return null;
  }
};

/**
 * Get the current view context
 * @returns {Promise<string|null>} - The view context, or null if not set
 */
export const getViewContext = async () => {
  try {
    const viewContext = await getSystemData(VIEW_CONTEXT_KEY, null);
    // logger.debug('getViewContext', 'Getting view context', { viewContext });
    fileLogger.debug('VIEWED_USER_STORE', 'Getting view context', { viewContext });
    return viewContext;
  } catch (error) {
    logger.error('getViewContext', 'Error getting view context', error, ErrorType.DATA_LOAD);
    fileLogger.error('VIEWED_USER_STORE', 'Error getting view context', { error: error.message });
    return null;
  }
};

/**
 * Clear the currently viewed user ID
 * @returns {Promise<void>}
 */
export const clearViewedUser = async () => {
  try {
    logger.info('clearViewedUser', 'Clearing viewed user');
    fileLogger.info('VIEWED_USER_STORE', 'Clearing viewed user');
    await clearFromDiskStorage(VIEWED_USER_KEY);
    await clearFromDiskStorage(VIEW_CONTEXT_KEY);
  } catch (error) {
    logger.error('clearViewedUser', 'Error clearing viewed user ID', error, ErrorType.DATA_SAVE);
    fileLogger.error('VIEWED_USER_STORE', 'Error clearing viewed user', { error: error.message });
  }
};

/**
 * Check if a team management view is active
 * @returns {Promise<boolean>} - True if a user is being viewed, false otherwise
 */
export const isInTeamManagement = async () => {
  try {
    const viewContext = await getViewContext();
    const result = viewContext === VIEW_CONTEXTS.TEAM_MANAGEMENT;
    // logger.debug('isInTeamManagement', 'Checking if in team management', { viewContext, result });
    return result;
  } catch (error) {
    logger.error('isInTeamManagement', 'Error checking if in team management', error, ErrorType.GENERAL);
    fileLogger.error('VIEWED_USER_STORE', 'Error checking if in team management', { error: error.message });
    return false;
  }
};
